import React, { useState, useEffect } from 'react';
import { X, Building2, MapPin, Phone, Mail, Globe, Upload, Save, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { clubService } from '../services/clubService';

const ClubForm = ({ isOpen, onClose, onSuccess, clubData = null, isEditing = false }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: clubData?.name || '',
    description: clubData?.description || '',
    address: clubData?.address || '',
    phone: clubData?.phone || '',
    email: clubData?.email || '',
    website: clubData?.website || '',
    logo: clubData?.logo || '',
    active: clubData?.active ?? true
  });
  
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(clubData?.logo || null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Verificar si el usuario puede ver el campo estado
  const canManageStatus = user?.role?.name === 'super_admin' || user?.role?.name === 'admin';

  // Actualizar el formulario cuando cambien los datos del club
  useEffect(() => {
    if (clubData) {
      setFormData({
        name: clubData.name || '',
        description: clubData.description || '',
        address: clubData.address || '',
        phone: clubData.phone || '',
        email: clubData.email || '',
        website: clubData.website || '',
        logo: clubData.logo || '',
        active: clubData.active ?? true
      });
      // Construir la URL completa del logo si existe
      if (clubData.logo) {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
        const logoUrl = clubData.logo.startsWith('http') 
          ? clubData.logo 
          : `${apiUrl}${clubData.logo}`;
        setLogoPreview(logoUrl);
      } else {
        setLogoPreview(null);
      }
    } else {
      setFormData({
        name: '',
        description: '',
        address: '',
        phone: '',
        email: '',
        website: '',
        logo: '',
        active: true
      });
      setLogoPreview(null);
    }
    setLogoFile(null);
    setErrors({});
  }, [clubData, isOpen]);

  const handleInputChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    
    if (type === 'file' && name === 'logo') {
      const file = files[0];
      if (file) {
        setLogoFile(file);
        // Crear preview
        const reader = new FileReader();
        reader.onloadend = () => {
          setLogoPreview(reader.result);
        };
        reader.readAsDataURL(file);
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
    
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'El nombre del club es obligatorio';
    }

    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'El email no tiene un formato válido';
    }

    if (formData.website && !/^https?:\/\/.+/.test(formData.website)) {
      newErrors.website = 'La URL del sitio web debe comenzar con http:// o https://';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      // Preparar datos para enviar - excluir logo del formData inicialmente
      const { logo, ...restFormData } = formData;
      const dataToSend = { ...restFormData };
      
      // Si hay un archivo de logo, agregarlo al dataToSend
      if (logoFile) {
        dataToSend.logo = logoFile;
      }
      // Si no hay logoFile, no agregar el campo logo (se mantiene el existente si es edición)
      
      // Llamar al servicio real para crear o actualizar el club
      const result = isEditing 
        ? await clubService.updateClub(clubData.id, dataToSend)
        : await clubService.createClub(dataToSend);
      
      // Si se actualizó el logo, actualizar el preview con la nueva URL del servidor
      if (logoFile && result.logo) {
        // Construir la URL completa del logo
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
        const logoUrl = result.logo.startsWith('http') ? result.logo : `${apiUrl}${result.logo}`;
        setLogoPreview(logoUrl);
      }
      
      // Pasar el resultado completo (incluyendo el ID generado por el backend)
      onSuccess?.(result);
      onClose();
    } catch (error) {
      console.error('Error al guardar el club:', error);
      const errorMessage = error.response?.data?.message || 'Error al guardar el club. Inténtalo de nuevo.';
      setErrors({ submit: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      address: '',
      phone: '',
      email: '',
      website: '',
      logo: '',
      active: true
    });
    setLogoFile(null);
    setLogoPreview(null);
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <Building2 size={24} className="text-blue-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">
              {isEditing ? 'Editar Club' : 'Crear Nuevo Club'}
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Formulario */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error general */}
          {errors.submit && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
              <AlertCircle size={20} className="text-red-600 mr-3" />
              <span className="text-red-800">{errors.submit}</span>
            </div>
          )}

          {/* Información básica */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Información Básica</h3>
            
            {/* Nombre */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Nombre del Club *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.name ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Ej: Club de Running Madrid"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name}</p>
              )}
            </div>

            {/* Descripción */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                Descripción
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Describe brevemente el club y sus actividades..."
              />
            </div>
          </div>

          {/* Información de contacto */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Información de Contacto</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Dirección */}
              <div>
                <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
                  <MapPin size={16} className="inline mr-1" />
                  Dirección
                </label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Calle, número, ciudad..."
                />
              </div>

              {/* Teléfono */}
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  <Phone size={16} className="inline mr-1" />
                  Teléfono
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="+34 123 456 789"
                />
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  <Mail size={16} className="inline mr-1" />
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.email ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="contacto@club.com"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                )}
              </div>

              {/* Website */}
              <div>
                <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-2">
                  <Globe size={16} className="inline mr-1" />
                  Sitio Web
                </label>
                <input
                  type="url"
                  id="website"
                  name="website"
                  value={formData.website}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.website ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="https://www.club.com"
                />
                {errors.website && (
                  <p className="mt-1 text-sm text-red-600">{errors.website}</p>
                )}
              </div>
            </div>
          </div>

          {/* Logo */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Logo del Club</h3>
            <div>
              <label htmlFor="logo" className="block text-sm font-medium text-gray-700 mb-2">
                <Upload size={16} className="inline mr-1" />
                Subir Logo
              </label>
              
              {/* Preview del logo */}
              {logoPreview && (
                <div className="mb-4">
                  <img 
                    src={logoPreview} 
                    alt="Preview del logo" 
                    className="h-32 w-32 object-contain border border-gray-300 rounded-lg p-2 bg-gray-50"
                  />
                </div>
              )}
              
              <input
                type="file"
                id="logo"
                name="logo"
                accept="image/jpeg,image/png,image/jpg,image/gif,image/svg+xml"
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              <p className="mt-1 text-sm text-gray-500">
                Sube una imagen del logo del club (JPG, PNG, GIF o SVG, máximo 2MB)
              </p>
            </div>
          </div>

          {/* Estado del Club - Solo para super_admin y admin */}
          {canManageStatus && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Estado del Club</h3>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="active"
                  name="active"
                  checked={formData.active}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="active" className="ml-2 block text-sm text-gray-700">
                  Club activo
                </label>
              </div>
              <p className="text-sm text-gray-500">
                Los clubs inactivos no aparecerán en las búsquedas y no podrán crear eventos.
              </p>
            </div>
          )}

          {/* Botones */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Guardando...
                </>
              ) : (
                <>
                  <Save size={16} className="mr-2" />
                  {isEditing ? 'Actualizar Club' : 'Crear Club'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ClubForm;
